import React, { useState, useMemo, useEffect } from 'react'
import { Flex } from '../../Components/Common/Flex'
import { Text } from '../../Components/Common/Text'
import { Button } from '../../Components/Common/Button'
import { Spinner } from '../../Components/Common/Spinner'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { ColumnGroup } from 'primereact/columngroup'
import { Row } from 'primereact/row'
import { Tag } from 'primereact/tag'
import { InputText } from 'primereact/inputtext'
import { useCQuery, invalidateQuery } from '../../Services/QueryCache'
import { columnProps } from '../../Services/Functions'
import { FC } from '../../Services/FeathersClient'

const getFolderCount = (section, folderKey) => section?.folders?.[folderKey]?.count || 0
const getAccounts = (section, folderKey) => section?.folders?.[folderKey]?.accounts
const sectionKeys = ['estrattiContiBanca', 'estrattiContiCarta', 'fattureNoXml', 'stipendi', 'fattureProvvigioni']

const cellCenter = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 40,
  width: '100%'
}
const cellStart = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 40,
  width: '100%',
  paddingLeft: 4
}

const detailRowGrid = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '4px 12px',
  alignItems: 'center',
  padding: '6px 0',
  borderBottom: '1px solid color-mix(in srgb, var(--surface-border, #444) 40%, transparent)',
  fontSize: 13
}

const CARD_W = 300
const cardShell = {
  border: '1px solid var(--surface-border, rgba(255,255,255,0.12))',
  borderRadius: 10,
  padding: '14px 16px',
  background: 'var(--surface-ground, rgba(0,0,0,0.15))',
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
  maxWidth: CARD_W
}

const buildClientRows = (data) => {
  if (!data?.clients) return []
  return data.clients.map(client => {
    const s = client.sections || {}
    let totaleDaGestire = 0
    let totaleInLavorazione = 0
    for (const key of sectionKeys) {
      totaleDaGestire += getFolderCount(s[key], 'daGestire')
      totaleInLavorazione += getFolderCount(s[key], 'inLavorazione')
    }
    const xml = client.fattureXml || null
    const mailData = client.mail || null
    return {
      ...client,
      totaleDaGestire,
      totaleInLavorazione,
      totalDocuments: client.totalDocuments || 0,
      xmlDaGestire: xml?.daGestire || 0,
      xmlSospese: xml?.sospese || 0,
      mailNonLette: mailData?.nonLette || 0,
      mailTotali: mailData?.totali || 0
    }
  })
}

const StatBlock = ({ icon, value, label, color }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px'
  }}
  >
    <i className={icon} style={{ fontSize: 14, color, flexShrink: 0 }} />
    <span style={{
      fontSize: 18,
      fontWeight: 700,
      color,
      fontVariantNumeric: 'tabular-nums',
      lineHeight: 1
    }}
    >{value ?? 0}
    </span>
    <span style={{
      fontSize: 11,
      color: 'var(--text-color-secondary)',
      lineHeight: 1.2,
      whiteSpace: 'nowrap'
    }}
    >{label}
    </span>
  </div>
)

const SectionDetail = ({ section, title }) => {
  if (!section) return null
  const dgAccounts = getAccounts(section, 'daGestire')
  const ilAccounts = getAccounts(section, 'inLavorazione')
  const dgCount = getFolderCount(section, 'daGestire')
  const ilCount = getFolderCount(section, 'inLavorazione')
  if (!dgCount && !ilCount) return null
  const hasDgAccounts = dgAccounts && Object.values(dgAccounts).some(v => v > 0)
  const hasIlAccounts = ilAccounts && Object.values(ilAccounts).some(v => v > 0)

  return (
    <div style={cardShell}>
      <div title={title} style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: hasDgAccounts || hasIlAccounts ? 12 : 0, fontSize: 13 }}>
        <div style={{ color: dgCount > 0 ? 'var(--orange-500, #f59e0b)' : 'var(--text-color-secondary)', fontWeight: dgCount > 0 ? 600 : 400 }}>
          Da Gestire: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{dgCount}</span>
        </div>
        <div style={{ color: ilCount > 0 ? 'var(--blue-500, #3b82f6)' : 'var(--text-color-secondary)', fontWeight: ilCount > 0 ? 600 : 400, textAlign: 'right' }}>
          In Lavorazione: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{ilCount}</span>
        </div>
      </div>
      {hasDgAccounts && (
        <div style={{ marginBottom: hasIlAccounts ? 12 : 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--orange-500, #f59e0b)', marginBottom: 8, letterSpacing: '0.02em' }}>DETTAGLIO DA GESTIRE</div>
          {Object.entries(dgAccounts).filter(([, v]) => v > 0).map(([name, count]) => (
            <div key={name} style={detailRowGrid}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
              <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{count}</span>
            </div>
          ))}
        </div>
      )}
      {hasIlAccounts && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--blue-500, #3b82f6)', marginBottom: 8, letterSpacing: '0.02em' }}>DETTAGLIO IN LAVORAZIONE</div>
          {Object.entries(ilAccounts).filter(([, v]) => v > 0).map(([name, count]) => (
            <div key={name} style={detailRowGrid}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
              <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const dash = <span style={{ color: 'var(--text-color-secondary)' }}>-</span>
const zero = <span style={{ color: 'var(--text-color-secondary)', fontVariantNumeric: 'tabular-nums' }}>0</span>

const groupBarStyle = (color) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '8px 12px',
  borderLeft: `3px solid ${color}`,
  borderRadius: 8,
  background: `color-mix(in srgb, ${color} 6%, transparent)`,
  flexWrap: 'wrap',
  minWidth: 0,
  flex: '1 1 320px'
})

const matchesClientSearch = (client, queryLower) => {
  if (!queryLower) return true
  const code = String(client.codClienteNerone ?? '').toLowerCase()
  const agency = String(client.agencyName ?? '').toLowerCase()
  const name = String(client.clientName ?? '').toLowerCase()
  return code.includes(queryLower) || agency.includes(queryLower) || name.includes(queryLower)
}

export const MonitorContabilita = () => {
  const { data, isLoading } = useCQuery('monitorContabilita')
  const [expandedRows, setExpandedRows] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [clientSearch, setClientSearch] = useState('')

  const allClients = useMemo(() => buildClientRows(data), [data])
  // Mostra tutti i clienti restituiti dall'API (cartella SharePoint e/o mail/XML), anche con 0 documenti
  const baseClients = useMemo(
    () => allClients.filter(c => c.codClienteNerone != null && c.codClienteNerone !== ''),
    [allClients]
  )

  const searchNorm = clientSearch.trim().toLowerCase()
  const clients = useMemo(
    () => baseClients.filter(c => matchesClientSearch(c, searchNorm)),
    [baseClients, searchNorm]
  )

  useEffect(() => {
    setExpandedRows(null)
  }, [searchNorm])

  const totals = useMemo(() => ({
    daGestire: allClients.reduce((s, c) => s + c.totaleDaGestire, 0),
    inLavorazione: allClients.reduce((s, c) => s + c.totaleInLavorazione, 0),
    documenti: data?.totalDocuments ?? allClients.reduce((s, c) => s + c.totalDocuments, 0),
    xmlDaGestire: allClients.reduce((s, c) => s + c.xmlDaGestire, 0),
    xmlSospese: allClients.reduce((s, c) => s + c.xmlSospese, 0),
    mailNonLette: allClients.reduce((s, c) => s + c.mailNonLette, 0),
    mailTotali: allClients.reduce((s, c) => s + c.mailTotali, 0)
  }), [allClients, data])

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await FC.service('monitorContabilita').find({ query: { refresh: 'true' } })
      invalidateQuery(['monitorContabilita'])
    } catch (e) {
      window.growl?.show({ severity: 'error', summary: 'Errore', detail: 'Errore durante il refresh dei dati' })
    } finally {
      setRefreshing(false)
    }
  }

  const lastUpdate = data?.lastUpdate ? new Date(data.lastUpdate).toLocaleString('it-IT') : '-'

  if (isLoading) return <Flex fw fh><Spinner /></Flex>

  const rowExpansionTemplate = (rowData) => {
    const s = rowData.sections || {}
    const sections = sectionKeys
      .map(key => ({ key, section: s[key] }))
      .filter(({ section }) => section && (getFolderCount(section, 'daGestire') > 0 || getFolderCount(section, 'inLavorazione') > 0))

    if (!sections.length) {
      return (
        <div style={{ padding: 16 }}>
          <span style={{ fontSize: 13, color: 'var(--text-color-secondary)', fontStyle: 'italic' }}>Nessun dettaglio disponibile.</span>
        </div>
      )
    }

    return (
      <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: `repeat(auto-fill, ${CARD_W}px)`, gap: 16, alignItems: 'start', justifyContent: 'start' }}>
        {sections.map(({ key, section }) => (
          <SectionDetail key={key} section={section} title={section?.label || key} />
        ))}
      </div>
    )
  }

  const ghStyle = (color) => ({
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.04em',
    color,
    padding: '8px 0',
    borderBottom: `2px solid ${color}`
  })
  const shStyle = {
    textAlign: 'center',
    fontSize: 12,
    padding: '6px 0'
  }

  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column header='' rowSpan={2} headerStyle={{ width: '3rem', padding: 0 }} />
        <Column header='Cod.' rowSpan={2} headerStyle={{ ...shStyle, width: '5rem' }} />
        <Column header='Cliente' rowSpan={2} headerStyle={{ ...shStyle, textAlign: 'left' }} />
        <Column header='DOCUMENTI SHAREPOINT' colSpan={3} headerStyle={ghStyle('#e8a230')} />
        <Column header='FATTURE XML' colSpan={2} headerStyle={ghStyle('#10b981')} />
        <Column header='EMAIL' colSpan={2} headerStyle={ghStyle('#8b5cf6')} />
      </Row>
      <Row>
        <Column header='Da Gestire' sortable field='totaleDaGestire' headerStyle={shStyle} />
        <Column header='In Lavoraz.' sortable field='totaleInLavorazione' headerStyle={shStyle} />
        <Column header='Totale' sortable field='totalDocuments' headerStyle={shStyle} />
        <Column header='Da Gestire' sortable field='xmlDaGestire' headerStyle={shStyle} />
        <Column header='Sospese' sortable field='xmlSospese' headerStyle={shStyle} />
        <Column header='Non Lette' sortable field='mailNonLette' headerStyle={shStyle} />
        <Column header='Totale' sortable field='mailTotali' headerStyle={shStyle} />
      </Row>
    </ColumnGroup>
  )

  const cs = (w, extra) => ({
    textAlign: 'center',
    verticalAlign: 'middle',
    border: 'none',
    width: w,
    padding: '0 4px',
    ...extra
  })

  return (
    <Flex fw as>
      <Flex row jb fw style={{ marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <Text value='Monitor Contabilità' bold title size={22} />
          <Text value={`Ultimo aggiornamento: ${lastUpdate}`} size={12} style={{ color: 'var(--text-color-secondary)', marginTop: 2 }} />
        </div>
        <Button
          label={refreshing ? 'Aggiornamento...' : 'Aggiorna Dati'}
          icon={refreshing ? 'pi pi-spin pi-spinner' : 'fa-duotone fa-arrows-rotate'}
          onClick={handleRefresh}
          disabled={refreshing}
        />
      </Flex>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 16,
          flexWrap: 'wrap',
          width: '100%',
          maxWidth: 560
        }}
      >
        <i className='pi pi-search' style={{ color: 'var(--text-color-secondary)', fontSize: 15 }} aria-hidden />
        <InputText
          id='monitor-contabilita-client-search'
          value={clientSearch}
          onChange={(e) => setClientSearch(e.target.value)}
          placeholder='Cerca cliente (nome, ragione sociale o codice)...'
          style={{ flex: '1 1 220px', minWidth: 0 }}
          aria-label='Cerca cliente nella tabella'
        />
        {clientSearch.trim() !== '' && (
          <>
            <Button
              label='Pulisci'
              icon='pi pi-times'
              onClick={() => setClientSearch('')}
              style={{ flexShrink: 0 }}
            />
            <Text
              size={12}
              value={`${clients.length} di ${baseClients.length}`}
              style={{ color: 'var(--text-color-secondary)', whiteSpace: 'nowrap' }}
            />
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', width: '100%' }}>
        <div style={groupBarStyle('#e8a230')}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', color: '#e8a230', marginRight: 4 }}>SHAREPOINT</span>
          <StatBlock icon='fa-duotone fa-inbox' value={totals.daGestire} label='Da Gestire' color='#e8a230' />
          <StatBlock icon='fa-duotone fa-gears' value={totals.inLavorazione} label='In Lavoraz.' color='#3b82f6' />
          <StatBlock icon='fa-duotone fa-files' value={totals.documenti} label='Totale' color='var(--text-color)' />
        </div>
        <div style={groupBarStyle('#10b981')}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', color: '#10b981', marginRight: 4 }}>XML</span>
          <StatBlock icon='fa-duotone fa-file-code' value={totals.xmlDaGestire} label='Da Gestire' color='#10b981' />
          <StatBlock icon='fa-duotone fa-file-exclamation' value={totals.xmlSospese} label='Sospese' color='#ef4444' />
        </div>
        <div style={groupBarStyle('#8b5cf6')}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', color: '#8b5cf6', marginRight: 4 }}>EMAIL</span>
          <StatBlock icon='fa-duotone fa-envelope' value={totals.mailNonLette} label='Non Lette' color='#8b5cf6' />
          <StatBlock icon='fa-duotone fa-envelopes' value={totals.mailTotali} label='Totale' color='var(--text-color)' />
        </div>
      </div>

      <DataTable
        value={clients}
        dataKey='codClienteNerone'
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        style={{ width: '100%' }}
        filterDisplay='row'
        className='TableSignatures monitor-contabilita-table'
        emptyMessage={searchNorm ? 'Nessun cliente corrisponde alla ricerca' : 'Nessun cliente trovato'}
        responsiveLayout='scroll'
        paginator rows={20}
        sortField='totaleDaGestire'
        sortOrder={-1}
        headerColumnGroup={headerGroup}
      >
        <Column expander style={{ width: '3rem' }} />
        <Column field='codClienteNerone' {...columnProps('Cod.')} sortable style={cs('5rem')} body={(row) => <div style={cellCenter}><span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{row.codClienteNerone}</span></div>} />
        <Column field='clientName' {...columnProps('Cliente')} sortable style={{ textAlign: 'left', verticalAlign: 'middle', border: 'none' }} body={(row) => <div style={cellStart}>{row.agencyName || row.clientName}</div>} />

        <Column field='totaleDaGestire' sortable style={cs('7rem', { borderLeft: '2px solid rgba(232,162,48,0.3)' })} body={(row) => <div style={cellCenter}>{row.totaleDaGestire > 0 ? <Tag severity='warning' value={row.totaleDaGestire} style={{ fontSize: '0.85rem' }} /> : row.totalDocuments > 0 ? zero : dash}</div>} />
        <Column field='totaleInLavorazione' sortable style={cs('7rem')} body={(row) => <div style={cellCenter}>{row.totaleInLavorazione > 0 ? <Tag severity='info' value={row.totaleInLavorazione} style={{ fontSize: '0.85rem' }} /> : row.totalDocuments > 0 ? zero : dash}</div>} />
        <Column field='totalDocuments' sortable style={cs('6rem')} body={(row) => <div style={cellCenter}>{row.totalDocuments > 0 ? <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{row.totalDocuments}</span> : dash}</div>} />

        <Column field='xmlDaGestire' sortable style={cs('7rem', { borderLeft: '2px solid rgba(16,185,129,0.3)' })} body={(row) => <div style={cellCenter}>{row.xmlDaGestire > 0 ? <Tag style={{ fontSize: '0.85rem', background: '#10b981' }} value={row.xmlDaGestire} /> : row.codSocieta ? zero : dash}</div>} />
        <Column field='xmlSospese' sortable style={cs('6rem')} body={(row) => <div style={cellCenter}>{row.xmlSospese > 0 ? <Tag severity='danger' value={row.xmlSospese} style={{ fontSize: '0.85rem' }} /> : row.codSocieta ? zero : dash}</div>} />

        <Column field='mailNonLette' sortable style={cs('7rem', { borderLeft: '2px solid rgba(139,92,246,0.3)' })} body={(row) => <div style={cellCenter}>{row.mailNonLette > 0 ? <Tag style={{ fontSize: '0.85rem', background: '#8b5cf6' }} value={row.mailNonLette} /> : row.mail ? zero : dash}</div>} />
        <Column field='mailTotali' sortable style={cs('6rem')} body={(row) => <div style={cellCenter}>{row.mailTotali > 0 ? <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{row.mailTotali}</span> : row.mail ? zero : dash}</div>} />
      </DataTable>
    </Flex>
  )
}
